import { financeClient, normaliseList } from './financeClient';

// All finance endpoints live under /api/admin/* (routes/ownerCrud.js financeRouter) and are
// platform-wide (every row carries a `vertical`). New v6 params (page/limit/q/vertical/overdue/
// hasReminders) are all optional — a pre-v6 backend ignores them and returns a bare array, which
// normaliseList() still copes with.

const clean = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    if (Array.isArray(v)) { if (v.length) out[k] = v.join(','); return; }
    out[k] = v;
  });
  return out;
};

// ---- 1. Property visit fees (RentalPlatformCharge) ----
export async function listPlatformCharges(params) {
  return normaliseList(await financeClient.get('/api/admin/platform-charges', { params: clean(params) }), params?.limit);
}
export async function decidePlatformCharge(id, payload) {
  return (await financeClient.patch(`/api/admin/platform-charges/${id}`, payload)).data;
}

// ---- 2. Delivery & agent payments (RentalDeliveryPayable) ----
export async function listDeliveryPayables(params) {
  return normaliseList(await financeClient.get('/api/admin/delivery-payables', { params: clean(params) }), params?.limit);
}
export async function remindDeliveryPayableOwner(id) {
  return (await financeClient.post(`/api/admin/delivery-payables/${id}/remind-owner`)).data;
}
export async function resolveDeliveryPayable(id, payload) {
  return (await financeClient.patch(`/api/admin/delivery-payables/${id}`, payload)).data;
}

// ---- 3. Shared-delivery billing / B2B (RentalInterBusinessDeliveryCharge) ----
export async function listInterBusinessCharges(params) {
  return normaliseList(await financeClient.get('/api/admin/inter-business-charges', { params: clean(params) }), params?.limit);
}
export async function remindInterBusinessOwner(id) {
  return (await financeClient.post(`/api/admin/inter-business-charges/${id}/remind-owner`)).data;
}
export async function resolveInterBusinessCharge(id, payload) {
  return (await financeClient.patch(`/api/admin/inter-business-charges/${id}`, payload)).data;
}

// ---- 4. Payments to reconcile (RentalBooking.payment.needsReconciliation) ----
export async function listPaymentsToReconcile(params) {
  return normaliseList(await financeClient.get('/api/admin/payments-to-reconcile', { params: clean(params) }), params?.limit);
}
export async function confirmReconciliation(id, note) {
  return (await financeClient.patch(`/api/admin/payments-to-reconcile/${id}/confirm`, { note })).data;
}

// ---- 5. Billing adjustments (RentalReconciliation) ----
export async function listReconciliations(params) {
  return normaliseList(await financeClient.get('/api/admin/reconciliations', { params: clean(params) }), params?.limit);
}
export async function decideReconciliation(id, payload) {
  return (await financeClient.patch(`/api/admin/reconciliations/${id}`, payload)).data;
}

// ---- 6. Refunds pending (RentalBooking.depositStatus = REFUND_PENDING) ----
export async function listRefundPending(params) {
  return normaliseList(await financeClient.get('/api/admin/refund-pending', { params: clean(params) }), params?.limit);
}
export async function markRefundSent(id, { method, notes } = {}) {
  return (await financeClient.patch(`/api/admin/refund-pending/${id}`, { method, notes })).data;
}

// ---- 7. Settlements (RentalSettlement) ----
export async function listSettlements(params) {
  return normaliseList(await financeClient.get('/api/admin/settlements', { params: clean(params) }), params?.limit);
}
export async function getSettlement(id) {
  return (await financeClient.get(`/api/admin/settlements/${id}`)).data;
}
export async function decideSettlement(id, action, payload) {
  return (await financeClient.patch(`/api/admin/settlements/${id}/${action}`, payload)).data;
}

// ---- 8. Settlement configuration (RentalSettlementConfig singleton) ----
export async function getSettlementConfig() {
  const res = await financeClient.get('/api/admin/settlement-config');
  return res.data?.data || res.data;
}
export async function updateSettlementConfig(payload) {
  const res = await financeClient.put('/api/admin/settlement-config', payload);
  return res.data?.data || res.data;
}

// ---- 9. Owner-specific finance overrides (RentalUser.financeOverrides) ----
// The owners list + typeahead behind the Owner Overrides picker. `withOverrides` returns only
// owners that actually have a negotiated term set; `q` is a free-text name/email match.
export async function listOwners({ q, withOverrides } = {}) {
  const params = {};
  if (q) params.q = q;
  if (withOverrides) params.withOverrides = 1;
  const res = await financeClient.get('/api/admin/owners', { params });
  return res.data?.data || [];
}
// Returns the full envelope { data: <overrides>, owner: { _id, name, email } } so the caller can
// show which account it resolved (a name lookup may have matched by name, not id/email).
export async function getOwnerFinanceOverrides(ownerId) {
  const res = await financeClient.get(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`);
  return res.data;
}
export async function updateOwnerFinanceOverrides(ownerId, payload) {
  const res = await financeClient.put(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`, payload);
  return res.data;
}

// ---- v6 new: lean staff profile (selfie / KYC) for the detail popups ----
export async function getFinanceOrder(id) {
  const res = await financeClient.get(`/api/admin/orders/${id}`);
  return res.data?.data || res.data;
}

export async function getStaff(id) {
  const res = await financeClient.get(`/api/admin/staff/${id}`);
  return res.data?.data || res.data;
}

// ---- v6 new: Part B — important pending items ----
export async function listPendingItems(params) {
  return normaliseList(await financeClient.get('/api/admin/pending-items', { params: clean(params) }), params?.limit);
}

// ---- manual_vs_automatic_invoicing.md — platform invoice register + owner-invoice verification ----
export async function listInvoices(params) {
  return normaliseList(await financeClient.get('/api/admin/invoices', { params: clean(params) }), params?.limit);
}
export async function getInvoiceRegisterSummary() {
  return (await financeClient.get('/api/admin/invoices/summary')).data?.data;
}
export async function verifyInvoice(id, payload) {
  return (await financeClient.patch(`/api/admin/invoices/${id}/verify`, payload)).data;
}
// Blob download — the axios interceptor adds the auth header the raw <a href> can't carry.
export async function downloadInvoicePdf(id, invoiceNumber) {
  const res = await financeClient.get(`/api/admin/invoices/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${String(invoiceNumber || id).replace(/[^a-z0-9-]+/gi, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
